import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { AssetStatus, UserListItem, Visibility } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useT } from "../i18n";
import { Select } from "../components/Select";

const PROFILES = ["240p", "480p", "720p", "1080p", "1440p", "2160p"];
const DEFAULT_PROFILES = ["240p", "480p", "720p"];

type Stage = "form" | "uploading" | "ingesting" | "done" | "error";

export function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useT();
  const fileRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [titles, setTitles] = useState<string[]>([]);

  function onFilesChange(newFiles: FileList | null) {
    const arr = newFiles ? Array.from(newFiles) : [];
    setFiles(arr);
    setTitles(arr.map((f) => f.name.replace(/\.[^/.]+$/, "")));
  }
  const [monetization, setMonetization] = useState("free");
  const [requiredTier, setRequiredTier] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("private");
  const [visibleToIds, setVisibleToIds] = useState<string[]>([]);
  const [tenantUsers, setTenantUsers] = useState<UserListItem[]>([]);
  const [profiles, setProfiles] = useState<string[]>(DEFAULT_PROFILES);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [stage, setStage] = useState<Stage>("form");
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<AssetStatus | "">("");
  const [assetId, setAssetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listUsers(100).then((res) => setTenantUsers(res.data)).catch(() => {});
  }, []);

  if (user && user.role === "viewer") {
    return (
      <div className="px-4 py-10 text-center text-brand-muted">
        {t("upload.noPermission")}
      </div>
    );
  }

  function toggleProfile(p: string) {
    setProfiles((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (files.length === 0) return;
    setError(null);
    setStage("uploading");
    setProgress(0);

    const allValid = titles.every((t) => t.trim());
    if (!allValid) {
      setError(t("upload.fillAllTitles"));
      setStage("error");
      return;
    }

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const currentTitle = titles[i]?.trim() || file.name.replace(/\.[^/.]+$/, "");

        // 1. Create asset → presigned upload URL.
        const MULTIPART_THRESHOLD = 8 * 1024 * 1024;
        const useMultipart = file.size > MULTIPART_THRESHOLD;
        const created = await api.createAsset({
          title: currentTitle,
          monetization,
          requiredTier: requiredTier || undefined,
          profiles,
          visibility,
          visibleToIds: visibility === "protected" ? visibleToIds : undefined,
          fileSize: file.size,
          multipart: useMultipart,
          tags: tags.length > 0 ? tags : undefined,
        });

        // 2. Upload the file.
        if (created.multipart) {
          await api.uploadMultipart(
            created.assetId,
            file,
            created.multipart.partUrls,
            created.multipart.partSize,
            created.multipart.uploadId,
            (r) => setProgress(r),
          );
        } else {
          await api.uploadFile(created.uploadUrl, file, (r) => setProgress(r));
        }

        // 3. Trigger ingest.
        setStage("ingesting");
        const res = await api.ingestAsset(created.assetId);
        setAssetId(created.assetId);
        setStatus(res.status as AssetStatus);
      }

      setStage("done");
    } catch (err) {
      setError((err as Error).message);
      setStage("error");
    }
  }

  function pollStatus(id: string) {
    const timer = setInterval(async () => {
      try {
        const a = await api.getAsset(id, locale);
        setStatus(a.status);
        if (a.status === "READY") {
          clearInterval(timer);
          setStage("done");
        } else if (a.status === "FAILED") {
          clearInterval(timer);
          setStage("error");
          setError(t("upload.transcodeFailed"));
        }
      } catch (err) {
        clearInterval(timer);
        setError((err as Error).message);
        setStage("error");
      }
    }, 2500);
  }

  const busy = stage === "uploading" || stage === "ingesting";

  const steps = [t("upload.step.details"), t("upload.step.upload"), t("upload.step.publish"), t("upload.step.ready")];
  const stepIndex =
    stage === "form" ? 0
    : stage === "uploading" ? 1
    : stage === "ingesting" ? 2
    : stage === "done" ? 3
    : 0;

  return (
    <div className="px-4 py-4 max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold mb-2">{t("upload.title")}</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-6 text-xs">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center gap-1">
            <span
              className={`px-2.5 py-1 rounded-full ${
                i < stepIndex
                  ? "bg-green-700 text-white"
                  : i === stepIndex
                    ? "bg-brand-red text-white"
                    : "bg-brand-surface text-brand-muted"
              }`}
            >
              {i + 1}. {label}
            </span>
            {i < steps.length - 1 && <span className="text-brand-line">→</span>}
          </div>
        ))}
      </div>

      {stage === "done" && assetId && (
        <div className="mb-6 p-4 rounded-xl bg-green-900/30 border border-green-700">
          <p className="text-green-300 font-medium">{t("upload.ready")}</p>
          <div className="flex gap-3 mt-3">
            <button
              onClick={() => navigate(`/watch/${assetId}`)}
              className="px-4 py-2 rounded bg-brand-red hover:bg-red-700 text-sm font-medium"
            >
              {t("upload.watchNow")}
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded bg-brand-hover text-sm font-medium"
            >
              {t("upload.backToHome")}
            </button>
          </div>
        </div>
      )}

      {stage === "error" && error && (
        <div className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700 text-red-300 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {files.length === 1 ? (
          <div>
            <label className="block text-sm text-brand-muted mb-1">{t("upload.titleLabel")}</label>
            <input
              value={titles[0] || ""}
              onChange={(e) => setTitles((prev) => [e.target.value])}
              className="w-full px-3 py-2 rounded bg-brand-surface border border-brand-line focus:border-brand-red outline-none"
              required
              disabled={busy}
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm text-brand-muted mb-1">{t("upload.titlesLabel")}</label>
            <div className="space-y-2">
              {titles.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-brand-muted w-6 text-right shrink-0">{i + 1}.</span>
                  <input
                    value={t || ""}
                    onChange={(e) => setTitles((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
                    placeholder={files[i].name}
                    className="flex-1 px-3 py-2 rounded bg-brand-surface border border-brand-line focus:border-brand-red outline-none text-sm"
                    required
                    disabled={busy}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm text-brand-muted mb-1">{t("upload.videoFile")}</label>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => onFilesChange(e.target.files)}
              className="text-sm text-brand-muted file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-brand-hover file:text-brand-text"
              required
              disabled={busy}
            />
            {files.length > 0 && (
              <span className="text-xs text-brand-muted">
                {files.length} {files.length === 1 ? t("upload.file") : t("upload.files")} · {files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024} MB
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-brand-muted mb-1">{t("upload.monetization")}</label>
            <Select
              value={monetization}
              options={[
                { value: "free", label: t("monetization.free") },
                { value: "sub", label: t("monetization.sub") },
                { value: "svod", label: t("monetization.svod") },
                { value: "tvod", label: t("monetization.tvod") },
                { value: "ads", label: t("monetization.ads") },
              ]}
              onChange={setMonetization}
              disabled={busy}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-brand-muted mb-1">{t("upload.requiredTier")}</label>
            <input
              value={requiredTier}
              onChange={(e) => setRequiredTier(e.target.value)}
              placeholder={t("upload.requiredTierPlaceholder")}
              className="w-full px-3 py-2 rounded bg-brand-surface border border-brand-line outline-none"
              disabled={busy}
            />
          </div>
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-sm text-brand-muted mb-2">{t("upload.visibility")}</label>
          <div className="flex flex-wrap gap-3">
            {(["private", "public", "protected"] as const).map((v) => (
              <label
                key={v}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  visibility === v
                    ? "border-brand-red bg-brand-red/10 text-brand-text"
                    : "border-brand-line bg-brand-surface text-brand-muted"
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={v}
                  checked={visibility === v}
                  onChange={() => setVisibility(v)}
                  className="hidden"
                  disabled={busy}
                />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  {v === "public" && <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />}
                  {v === "private" && <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2z" />}
                  {v === "protected" && <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />}
                </svg>
                <span className="text-sm font-medium capitalize">{t(`visibility.${v}`)}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-brand-muted mt-1">
            {visibility === "public" && t("upload.visibility.public.desc")}
            {visibility === "private" && t("upload.visibility.private.desc")}
            {visibility === "protected" && t("upload.visibility.protected.desc")}
          </p>
        </div>

        {/* Protected user picker */}
        {visibility === "protected" && (
          <div>
            <label className="block text-sm text-brand-muted mb-2">{t("upload.grantAccess")}</label>
            {tenantUsers.length === 0 ? (
              <p className="text-xs text-brand-muted">{t("upload.loadingUsers")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tenantUsers
                  .filter((u) => u.id !== user?.id)
                  .map((u) => (
                    <label
                      key={u.id}
                      className={`px-3 py-1 rounded-full text-sm border cursor-pointer ${
                        visibleToIds.includes(u.id)
                          ? "bg-brand-red border-brand-red text-white"
                          : "bg-brand-surface border-brand-line text-brand-muted"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={visibleToIds.includes(u.id)}
                        onChange={() =>
                          setVisibleToIds((prev) =>
                            prev.includes(u.id)
                              ? prev.filter((x) => x !== u.id)
                              : [...prev, u.id],
                          )
                        }
                        disabled={busy}
                      />
                      {u.email}
                    </label>
                  ))}
              </div>
            )}
            {visibleToIds.length === 0 && (
              <p className="text-xs text-yellow-500 mt-1">{t("upload.selectUser")}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm text-brand-muted mb-2">{t("upload.renditionProfiles")}</label>
          <div className="flex flex-wrap gap-2">
            {PROFILES.map((p) => (
              <label
                key={p}
                className={`px-3 py-1 rounded-full text-sm border cursor-pointer ${
                  profiles.includes(p)
                    ? "bg-brand-red border-brand-red text-white"
                    : "bg-brand-surface border-brand-line text-brand-muted"
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={profiles.includes(p)}
                  onChange={() => toggleProfile(p)}
                  disabled={busy}
                />
                {p}
              </label>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm text-brand-muted mb-2">{t("upload.tags")}</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-brand-hover border border-brand-line"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                  disabled={busy}
                  className="text-brand-muted hover:text-brand-red"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagInput.trim()) {
                e.preventDefault();
                const t = tagInput.trim();
                if (!tags.includes(t)) setTags((prev) => [...prev, t]);
                setTagInput("");
              }
            }}
            placeholder={t("upload.tagPlaceholder")}
            disabled={busy}
            className="w-full px-3 py-2 rounded bg-brand-surface border border-brand-line focus:border-brand-red outline-none text-sm"
          />
        </div>

        {stage === "uploading" && (
          <div>
            <div className="text-sm text-brand-muted mb-1">
              {t("upload.uploading", { percent: Math.round(progress * 100) })}
            </div>
            <div className="w-full h-2 rounded bg-brand-line overflow-hidden">
              <div
                className="h-full bg-brand-red transition-all"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </div>
        )}

        {stage === "ingesting" && (
          <div className="text-sm text-brand-muted">
            {t("upload.ingesting", { status: status || "QUEUED" })}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || files.length === 0 || profiles.length === 0}
          className="px-5 py-2 rounded bg-brand-red hover:bg-red-700 disabled:opacity-40 font-medium"
        >
          {busy ? t("upload.processing") : t("upload.publish")}
        </button>
      </form>
    </div>
  );
}